// Copyright (c) Docugami, Inc. All rights reserved.

import { CoachMarkId } from "coachmarks/CoachMarkTypes";
import { InfoView } from "components/work_path/InfoView";
import styled from "styled-components";
import { useTrackedCoachMark } from "./TrackedCoachMark";
import AutoAssignCoachMarkUrl from "assets/AutoAssignCoachmark.png";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { BuildType } from "networking/BuildType";
import { AutomationId } from "utils/Automation";
import { isBuildActive } from "store/state_helpers";

const Container = styled.div`
    width: 100%;
    height: 100%;
    background: #FFFFFFE6;
    display: flex;
    justify-content: center;
    align-items: center;
`

const AutoAssignImage = styled.img`
    width: 125px;
`

const AssigningImage = styled.img`
    width: 125px;
`

export function useUnassignedFilesCoachmarks(unassignedFileCount: number): JSX.Element | null {
    const { value: autoAssignShown, setValue: setAutoAssignShown } = useTrackedCoachMark(CoachMarkId.AutoAssignDocSet);
    const builds = useSelector((state: AppState) => state.organizations.pipelineBuilds.activeBuilds);
    const isClusteringBuildActive = isBuildActive(BuildType.Clustering, builds);

    if (isClusteringBuildActive) {
        return <AutoAssigningCoachmark />
    }

    if (unassignedFileCount > 0 && !autoAssignShown) {
        return <AutoAssignCoachmark onConfirm={() => setAutoAssignShown(true)} />;
    }

    return null;
}

interface AutoAssignCoachmarkProps {
    onConfirm: () => void;
}

const AutoAssignCoachmark = (props: AutoAssignCoachmarkProps): JSX.Element => {
    const text = "Click AUTO ASSIGN above to have Docugami assign these documents to doc sets automatically.";
    const subtext = "You can also use ASSIGN TO to manually assign documents to the doc sets of your choice"
    return (
        <Container>
            <InfoView
                icon={<AutoAssignImage src={AutoAssignCoachMarkUrl} />}
                confirmAction={{ label: "Got it", action: props.onConfirm }}
                titleText={text}
                secondaryText={[subtext]} />
        </Container>
    )
}

const AutoAssigningCoachmark = (): JSX.Element => {
    const text = "Your unassigned files are actively being assigned to doc sets!"
    const subtext = 'You will receive an alert when assignment completes and your files will appear in the DOC SETS list on the left.'
    return (
        <Container>
            <InfoView
                icon={<AssigningImage src={AutoAssignCoachMarkUrl} />}
                titleText={text}
                secondaryText={[subtext]} />
        </Container>
    );
}

export const AddDocsAutoAssigningCoachmark = (): JSX.Element => {
    const text = "Docugami is now processing your documents into groups of similar docs called doc sets."
    const subtext = 'You can always modify your doc sets later.'
    return (
        <Container data-automation-id={AutomationId.DocExplorerAddDocsAutoAssigning}>
            <InfoView
                icon={<AssigningImage src={AutoAssignCoachMarkUrl} />}
                titleText={text}
                secondaryText={[subtext]} />
        </Container>
    );
}
